import { LegacyProductCode } from '@up2stay/up2promo-api-client';
import { Input, Label } from '@up2stay/up2promo-ui';
import { Link2 } from 'lucide-react';

import { useDictionary } from '@/core/providers/dictionary-provider';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';
import { ProductsSetupChoiceCard } from '@/routes/app/products-setup-wizard/products-setup-choice-card';
import { ProductsSetupStepShell } from '@/routes/app/products-setup-wizard/products-setup-step-shell';

export function ProductsSetupStepUsername({
  productType,
  value,
  onChange,
  onUseCustomUrl,
  error,
}: {
  productType: LegacyProductCode | undefined;
  value: string;
  onChange: (value: string) => void;
  onUseCustomUrl?: () => void;
  /** Shown while typing an invalid handle (DEV-566). */
  error?: string;
}) {
  const dict = useDictionary();
  const setup = (dict as { products?: { setup?: Record<string, string> } })
    .products?.setup;
  const isIg = productType === LegacyProductCode.IG;
  // Cap: autofocus + AnimatePresence + keyboard inset pads thrash the viewport.
  const autoFocus = !isCapacitorNative();

  return (
    <ProductsSetupStepShell
      title={
        isIg ? (setup?.usernameTitleIg ?? '') : (setup?.usernameTitleTt ?? '')
      }
      description={
        isIg ? setup?.usernameDescriptionIg : setup?.usernameDescriptionTt
      }
    >
      <div className="flex flex-col gap-2">
        <Label htmlFor="setup-username">{setup?.usernameLabel ?? ''}</Label>
        <Input
          id="setup-username"
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={
            isIg
              ? (setup?.usernamePlaceholderIg ?? '')
              : (setup?.usernamePlaceholderTt ?? '')
          }
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="done"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? 'setup-username-error' : undefined}
          className={error ? 'border-destructive' : undefined}
        />
        {error ? (
          <p
            id="setup-username-error"
            className="text-[13.5px] text-destructive"
          >
            {error}
          </p>
        ) : null}
      </div>

      {onUseCustomUrl ? (
        <ProductsSetupChoiceCard
          title={
            setup?.switchToCustomUrl ?? setup?.destinationCustomTitle ?? ''
          }
          description={setup?.destinationCustomDescription}
          icon={Link2}
          onClick={onUseCustomUrl}
        />
      ) : null}
    </ProductsSetupStepShell>
  );
}
